/**
 * Traducción de los enums de `@viva/shared` a español rioplatense y su **tono semántico**
 * para el `Badge`.
 *
 * Regla: los enums viven en `shared` (fuente única) y su texto de UI vive acá. Ningún
 * componente escribe un literal de estado a mano — así, cuando el modelo de datos agrega
 * un valor, hay un solo lugar que actualizar y TypeScript avisa si falta.
 */
import type {
  KioskPurchasePaymentMethod,
  KioskPurchaseStatus,
  PriceReviewReason,
  AccessProfile,
  AccessMethod,
  AccessPersonKind,
  AccessPointState,
  AccessPointType,
  AccessReason,
  AccessResult,
  Finger,
  TimeclockDirection,
  TimeclockMethod,
  AptState,
  BenefitKind,
  BenefitStatus,
  ReferralCreditStatus,
  BranchKind,
  CampaignStatus,
  CardBrand,
  CatalogKind,
  CommunicationSource,
  ContactPersonKind,
  ConversationStatus,
  ChargeKind,
  ChargeStatus,
  CorporateAgreementStatus,
  CorporateConditionKind,
  DeviceKind,
  DeviceStatus,
  DocType,
  ExternalInvoiceStatus,
  FamilyGroupStatus,
  IncompleteFlag,
  IntegrationEnvironment,
  LaunchChecklistKind,
  LaunchChecklistStatus,
  LaunchModule,
  LegalDocType,
  MedicalCertificateStatus,
  MemberStatus,
  MembershipStatus,
  ModerationAction,
  NotificationChannel,
  MigrationAdapter,
  MigrationRunKind,
  MigrationRunStatus,
  HireMode,
  PayrollLineKind,
  PayrollPendingFlag,
  PayrollSettlementStatus,
  PerformanceBarSource,
  StaffStatus,
  StaffType,
  TimeclockDiscrepancyKind,
  TimeclockResolution,
  WorkShiftKind,
  Platform,
  PlanPlatformRequirement,
  PostStatus,
  ProspectStatus,
  DebitAuthorizationStatus,
  FreezeKind,
  FreezeStatus,
  LocationScope,
  PaymentAgreementStatus,
  PaymentMethod,
  PaymentStatus,
  PlanKind,
  PriceListStatus,
  PriceMedium,
  PromotionType,
  RetentionSegment,
  BankFeedMatchStatus,
  BusinessAlertKind,
  CentralAllocationMode,
  ReportExportFormat,
  ReportMetric,
  BookingOrigin,
  BookingPersonKind,
  BookingStatus,
  CheckinMethod,
  ClassSessionStatus,
  EventKind,
  EventRegistrationStatus,
  EventStatus,
  FixedSlotStatus,
  PenaltyKind,
  RecoveryCreditOrigin,
  ScheduleStatus,
  ScheduledClassStatus,
  WaitlistStatus,
  ExpenseMethod,
  ExpenseStatus,
  InvoiceReceiverKind,
  InvoiceStatus,
  InvoiceType,
  PlatformSettlementStatus,
  SaleChannel,
  SaleStatus,
  StockCountStatus,
  StockMovementKind,
  SupplierPaymentMode,
  CashDifferenceKind,
  CashDifferenceStatus,
  CashMovementKind,
  CashShiftSlot,
  CashShiftStatus,
  EnvelopeStatus,
  PosBatchStatus,
  ReversalKind,
  SettingScope,
  SignatureMedium,
  SignerKind,
  Surface,
  UserRole,
  UserStatus,
  VisitorStatus,
  WithdrawalMode,
} from "@viva/shared";

/** Tono visual de un estado. `neutral` es el default honesto: no todo estado es bueno o malo. */
export type Tone = "neutral" | "positive" | "warning" | "danger" | "accent" | "info";

export const ROLE_LABEL: Record<UserRole, string> = {
  owner: "Dueño",
  admin: "Administración",
  intersede_coordinator: "Coordinador intersedes",
  branch_manager: "Encargado de sede",
  receptionist: "Recepcionista",
  teacher: "Profesor",
  accountant_readonly: "Contador (sólo lectura)",
};

/** Qué alcance tiene cada rol — se muestra en la matriz de permisos (RN-ROL-02). */
export const ROLE_SCOPE: Record<UserRole, string> = {
  owner: "Todo el club",
  admin: "Todo el club",
  intersede_coordinator: "Todas las sedes, sin financiero consolidado",
  branch_manager: "Su sede",
  receptionist: "Su sede",
  teacher: "Sus clases",
  accountant_readonly: "Lectura de facturación y comprobantes",
};

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  active: "Activo",
  deactivated: "Desactivado",
};

export const USER_STATUS_TONE: Record<UserStatus, Tone> = {
  active: "positive",
  deactivated: "danger",
};

export const SURFACE_LABEL: Record<Surface, string> = {
  admin_portal: "Portal admin",
  teachers_portal: "Portal profesores",
  members_app: "App de socios",
  kiosk: "Tablet kiosko",
  access_point: "Punto de acceso",
  system: "Sistema (automático)",
};

export const BRANCH_KIND_LABEL: Record<BranchKind, string> = {
  operational: "Operativa",
  central: "Central (virtual)",
};

export const ACCESS_POINT_TYPE_LABEL: Record<AccessPointType, string> = {
  turnstile: "Molinete",
  location_qr: "QR de local",
};

export const ACCESS_POINT_STATE_LABEL: Record<AccessPointState, string> = {
  online: "En línea",
  offline: "Fuera de línea",
  contingency: "Contingencia",
};

export const ACCESS_POINT_STATE_TONE: Record<AccessPointState, Tone> = {
  online: "positive",
  offline: "danger",
  contingency: "warning",
};

export const DEVICE_KIND_LABEL: Record<DeviceKind, string> = {
  kiosk_tablet: "Tablet kiosko",
  access_agent: "Agente de acceso",
};

export const DEVICE_STATUS_LABEL: Record<DeviceStatus, string> = {
  active: "Activo",
  revoked: "Revocado",
};

export const DEVICE_STATUS_TONE: Record<DeviceStatus, Tone> = {
  active: "positive",
  revoked: "danger",
};

export const SETTING_SCOPE_LABEL: Record<SettingScope, string> = {
  global: "Global",
  branch: "Por sede",
};

/**
 * Origen del valor efectivo de un parámetro (`GET /api/settings`).
 * Que se vea que un valor viene del **default del AF** y no de una carga es parte de la
 * honestidad del sistema (docs/decisiones/settings-versionados.md).
 */
export const SETTING_SOURCE_LABEL: Record<"branch" | "global" | "default", string> = {
  branch: "Cargado en esta sede",
  global: "Cargado global",
  default: "Valor por defecto",
};

export const SETTING_SOURCE_TONE: Record<"branch" | "global" | "default", Tone> = {
  branch: "accent",
  global: "info",
  default: "neutral",
};

/** Quién edita cada familia de claves (RN-ROL-03 y sus dos excepciones). */
export const SETTING_EDITORS_LABEL: Record<string, string> = {
  general: "Dueño y Coordinador intersedes",
  financial: "Dueño y Administración (mueve plata)",
  owner_only: "Sólo el Dueño (umbral financiero)",
  holiday_calendar: "Dueño, Administración, Coordinación y encargados de sede",
};

export const CATALOG_KIND_LABEL: Record<CatalogKind, string> = {
  expense_categories: "Categorías de gasto",
  contact_sources: "Orígenes de contacto",
  lost_reasons: "Motivos de pérdida",
  promotion_tags: "Etiquetas de promoción",
  termination_reasons: "Motivos de baja",
  contact_channels: "Canales de contacto",
};

/** Claves del catálogo de alertas de seguridad (RN-ROL-14, docs/api/roles.md). */
export const SECURITY_ALERT_LABEL: Record<string, string> = {
  payment_reversed: "Cobro anulado",
  sale_voided: "Venta anulada",
  discount_over_threshold: "Descuento sobre el umbral",
  cash_difference_over_threshold: "Diferencia de caja sobre el umbral",
  payment_corrected: "Pago corregido",
  access_exception_granted: "Ingreso autorizado con deuda",
  price_list_changed: "Cambio en la lista de precios",
  role_changed: "Cambio de rol o permisos",
  user_created_or_deactivated: "Alta o baja de usuario",
  mass_export: "Exportación masiva",
  special_freeze: "Congelamiento especial",
  failed_logins: "Intentos fallidos de login",
};

/** Umbral: qué unidad usa cada alerta (RN-ROL-15). */
export const SECURITY_ALERT_THRESHOLD_UNIT: Record<string, "percent" | "money" | "count" | null> = {
  payment_reversed: null,
  sale_voided: null,
  discount_over_threshold: "percent",
  cash_difference_over_threshold: "money",
  payment_corrected: null,
  access_exception_granted: "count",
  price_list_changed: null,
  role_changed: null,
  user_created_or_deactivated: null,
  mass_export: "count",
  special_freeze: null,
  failed_logins: "count",
};

export const CHANNEL_LABEL: Record<string, string> = {
  push: "Push",
  email: "Email",
  inapp: "Centro de avisos",
};

/** Acciones de auditoría más frecuentes (`auditLogs.action`). */
export const AUDIT_ACTION_LABEL: Record<string, string> = {
  "user.create": "Alta de usuario",
  "user.update": "Edición de usuario",
  "user.deactivate": "Desactivación de usuario",
  "user.activate": "Reactivación de usuario",
  "user.resetPassword": "Reseteo de contraseña",
  "user.changeActiveBranch": "Cambio de sede activa",
  "session.revoke": "Cierre remoto de sesión",
  "branch.create": "Alta de sede",
  "branch.update": "Edición de sede",
  "location.create": "Alta de local",
  "location.update": "Edición de local",
  "room.create": "Alta de salón",
  "room.update": "Edición de salón",
  "accessPoint.create": "Alta de punto de acceso",
  "accessPoint.update": "Edición de punto de acceso",
  "accessPoint.regenerateQr": "Regeneración del QR de local",
  "accessPoint.setState": "Cambio de estado del punto de acceso",
  "discipline.create": "Alta de disciplina",
  "discipline.update": "Edición de disciplina",
  "setting.update": "Cambio de parámetro",
  "catalog.update": "Edición de catálogo",
  "securityAlert.update": "Cambio de alerta de seguridad",
  "device.create": "Alta de dispositivo",
  "device.revoke": "Revocación de dispositivo",
  "job.setEnabled": "Encendido/apagado de una tarea automática",
  "job.runNow": "Corrida manual de una tarea automática",
  "auditLogs.read": "Consulta de la auditoría",
  "auditLogs.export": "Exportación de la auditoría",
  "branch.scope": "Acceso denegado por sede",

  // §3 Socios (AF §3, docs/api/socios.md)
  "member.create": "Alta de socio",
  "member.reactivate": "Reactivación del socio",
  "member.update": "Edición de la ficha",
  "member.updateIdentity": "Cambio de documento, nombre o nacimiento",
  "member.terminate": "Baja del socio",
  "member.admissionBan": "Derecho de admisión",
  "member.enrollFingerprint": "Enrolamiento de huella",
  "member.signConsents": "Firma de consentimientos",
  "member.merge": "Fusión de fichas duplicadas",
  "member.undoMerge": "Fusión deshecha",
  "members.export": "Exportación del padrón",
  "members.imageConsentExport": "Exportación de cesión de imagen NO",
  "medicalCertificate.create": "Carga de apto médico",
  "medicalCertificate.validate": "Validación de apto médico",
  "legalDocVersion.publish": "Publicación de un texto legal",
  "signedDocument.kioskSign": "Firma en la tablet",
  "familyGroup.create": "Alta de grupo familiar",
  "familyGroup.addMember": "Integrante sumado al grupo",
  "familyGroup.removeMember": "Integrante quitado del grupo",
  "familyGroup.setPayer": "Cambio de pagador del grupo",
  "corporateAgreement.create": "Alta de convenio",
  "corporateAgreement.update": "Edición de convenio",
  "corporateAgreement.addBeneficiary": "Beneficiario sumado al convenio",
  "corporateAgreement.removeBeneficiary": "Beneficiario dado de baja del convenio",
  "benefit.create": "Alta de cortesía, canje o descuento",
  "benefit.revoke": "Revocación del beneficio",
  "benefits.courtesyReport": "Consulta del reporte de cortesías",
  "visitor.create": "Alta express de visitante",
  "visitor.update": "Edición del visitante",
  "visitor.verify": "Verificación de vigencia y apto",
  "visitor.convert": "Conversión de visitante a socio",
  "visitors.entriesExport": "Exportación de ingresos de visitantes",
  "emergencyWithdrawal.create": "Alta de ficha de retiro",
  "emergencyWithdrawal.notifyContact": "Aviso al contacto de emergencia",
  "emergencyWithdrawal.close": "Cierre de la ficha de retiro",
  "emergencyWithdrawal.appendNote": "Nota agregada a la ficha de retiro",
  // §4 Planes y cobranzas
  "plan.create": "Alta de plan",
  "plan.update": "Edición de plan",
  "plan.deactivate": "Desactivación de plan",
  "priceList.create": "Alta de lista de precios",
  "priceList.update": "Edición de una lista en borrador",
  "priceList.publish": "Publicación de la lista de precios",
  "priceLists.history.read": "Consulta del historial de aumentos",
  "promotion.create": "Alta de promoción",
  "promotion.update": "Edición de promoción",
  "promotion.deactivate": "Desactivación de promoción",
  "membership.sell": "Venta de membresía",
  "membership.schedulePlanChange": "Cambio de plan programado",
  "membership.cancelPlanChange": "Cancelación del cambio de plan",
  "membership.transfer": "Transferencia de plan largo",
  "membership.addAddonPack": "Pack de clases extra",
  "charge.forgive": "Condonación de deuda",
  "debt.resendNotice": "Reenvío del aviso de deuda",
  "paymentAgreement.create": "Alta de acuerdo de pago",
  "freeze.vacation": "Congelamiento por vacaciones",
  "freeze.special": "Congelamiento especial",
  "freeze.lift": "Levantamiento de un congelamiento",
  "debitAuthorization.create": "Adhesión al débito automático",
  "debitAuthorization.retokenize": "Actualización de la tarjeta del débito",
  "debitAuthorization.cancel": "Baja del débito automático",
  "debitAttempt.manualRetry": "Reintento manual de un débito",
  "debits.run": "Corrida manual de débitos",
  "debits.export": "Exportación del panel de débitos",
  "debtors.export": "Exportación del listado de deudores",
};

/** Entidades auditables — nombre de colección → nombre en castellano. */
export const ENTITY_LABEL: Record<string, string> = {
  users: "Usuarios",
  sessions: "Sesiones",
  branches: "Sedes",
  locations: "Locales",
  rooms: "Salones",
  accessPoints: "Puntos de acceso",
  disciplines: "Disciplinas",
  settings: "Parámetros",
  catalogs: "Catálogos",
  devices: "Dispositivos",
  auditLogs: "Auditoría",
  members: "Socios",
  payments: "Pagos",
  cashMovements: "Movimientos de caja",
  visitors: "Visitantes",
  familyGroups: "Grupos familiares",
  corporateAgreements: "Convenios con empresas",
  benefits: "Cortesías, canjes y descuentos",
  medicalCertificates: "Aptos médicos",
  signedDocuments: "Documentos firmados",
  legalDocVersions: "Textos legales",
  emergencyWithdrawals: "Fichas de retiro",
  memberships: "Membresías",
  charges: "Cuotas",
  plans: "Planes",
  priceLists: "Listas de precios",
  promotions: "Promociones",
  freezes: "Congelamientos",
  paymentAgreements: "Acuerdos de pago",
  debitAuthorizations: "Adhesiones al débito",
  debitAttempts: "Intentos de débito",
  debitRuns: "Corridas de débito",
};

// ─────────────────────────────────────────────────────────────────────────────
// §4 Planes, precios, matrícula y cobranzas
// ─────────────────────────────────────────────────────────────────────────────

/** `plans.kind` — el catálogo del AF §4.1. El NOMBRE del plan lo pone el club; esto es su tipo. */
export const PLAN_KIND_LABEL: Record<PlanKind, string> = {
  monthly: "Mensual",
  fortnight: "Quincena",
  week: "Semana",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
  reformer_only: "Solo Reformer",
  trial: "Pase de prueba",
};

/** RN-ACC-11: a qué locales le abre la puerta un plan. */
export const LOCATION_SCOPE_LABEL: Record<LocationScope, string> = {
  all: "Todos los locales",
  pilates_only: "Sólo locales de Pilates",
  strength: "Sólo salas de musculación",
};

/** RN-PLA-02/33/34: qué incluye un plan, separado de su duración. */
export const ACCESS_PROFILE_LABEL: Record<AccessProfile, string> = {
  full: "Pase libre",
  disciplines_only: "Sólo algunas disciplinas",
  strength_only: "Musculación (sin clases)",
};

/** RN-PLA-10: `debit` es el **valor preferencial** del consentimiento de débito. */
export const PRICE_MEDIUM_LABEL: Record<PriceMedium, string> = {
  cash: "Efectivo",
  card: "Tarjeta / transferencia / MP",
  debit: "Débito automático",
};

export const PRICE_LIST_STATUS_LABEL: Record<PriceListStatus, string> = {
  draft: "Borrador",
  active: "Publicada",
  superseded: "Reemplazada",
};

export const PRICE_LIST_STATUS_TONE: Record<PriceListStatus, Tone> = {
  draft: "warning",
  active: "positive",
  superseded: "neutral",
};

export const PROMOTION_TYPE_LABEL: Record<PromotionType, string> = {
  discount_pct: "Descuento porcentual",
  fixed_amount: "Monto fijo",
  special_price: "Precio especial",
  bonus_days: "Días bonificados",
};

/** Filtro de la pantalla «Promociones» (`GET /api/promotions?status=`). */
export const PROMOTION_STATUS_LABEL: Record<string, string> = {
  current: "Vigentes",
  scheduled: "Programadas",
  expired: "Vencidas",
  inactive: "Desactivadas",
};

export const MEMBERSHIP_STATUS_LABEL: Record<MembershipStatus, string> = {
  active: "Activa",
  scheduled: "Programada",
  frozen: "Congelada",
  expired: "Vencida",
  transferred: "Transferida",
  canceled: "Cancelada",
};

export const MEMBERSHIP_STATUS_TONE: Record<MembershipStatus, Tone> = {
  active: "positive",
  scheduled: "info",
  frozen: "accent",
  expired: "neutral",
  transferred: "info",
  canceled: "neutral",
};

export const CHARGE_KIND_LABEL: Record<ChargeKind, string> = {
  membership_due: "Cuota mensual",
  plan_installment: "Cuota de plan largo",
  enrollment_fee: "Matrícula",
  class_extra: "Clase extra",
  event_fee: "Inscripción a evento",
  trial_class: "Clase de prueba",
  agreement_installment: "Cuota de acuerdo",
};

/** `in_collection` NO es deuda todavía: el bloqueo espera a los reintentos (RN-PLA-25). */
export const CHARGE_STATUS_LABEL: Record<ChargeStatus, string> = {
  pending: "Pendiente",
  in_collection: "En gestión de cobro",
  paid: "Pagada",
  overdue: "Vencida",
  forgiven: "Condonada",
  reversed: "Revertida",
};

export const CHARGE_STATUS_TONE: Record<ChargeStatus, Tone> = {
  pending: "neutral",
  in_collection: "warning",
  paid: "positive",
  overdue: "danger",
  forgiven: "info",
  reversed: "neutral",
};

/** `account.status` de `GET /api/members/:id/account` (AF §4, pantalla de cuenta corriente). */
export type AccountStatus =
  "up_to_date" | "in_tolerance" | "in_debt" | "blocked" | "frozen" | "in_collection";

export const ACCOUNT_STATUS_LABEL: Record<AccountStatus, string> = {
  up_to_date: "Al día",
  in_tolerance: "En tolerancia",
  in_debt: "En deuda",
  blocked: "Bloqueado",
  frozen: "Congelado",
  in_collection: "En gestión de cobro",
};

export const ACCOUNT_STATUS_TONE: Record<AccountStatus, Tone> = {
  up_to_date: "positive",
  in_tolerance: "warning",
  in_debt: "warning",
  blocked: "danger",
  frozen: "accent",
  in_collection: "info",
};

/** Por qué cada estado es lo que es: la pantalla lo dice, no lo deja adivinar. */
export const ACCOUNT_STATUS_HINT: Record<AccountStatus, string> = {
  up_to_date: "Sin cuotas vencidas: ingresa normalmente.",
  in_tolerance: "Venció pero está dentro de la tolerancia de mora: todavía ingresa.",
  in_debt: "Tiene deuda vencida y un acuerdo de pago al día, así que no está bloqueado.",
  blocked: "Deuda fuera de la tolerancia: el molinete lo frena en TODAS las sedes.",
  frozen: "Congelamiento vigente: el vencimiento corrió por los días otorgados.",
  in_collection: "Débito rechazado con reintentos programados: el bloqueo espera a que se agoten.",
};

export const FREEZE_KIND_LABEL: Record<FreezeKind, string> = {
  vacation: "Vacaciones",
  special: "Especial (excepción del dueño)",
};

export const FREEZE_STATUS_LABEL: Record<FreezeStatus, string> = {
  requested: "Solicitado",
  active: "Vigente",
  finished: "Terminado",
  rejected: "Rechazado",
  lifted: "Levantado antes de tiempo",
};

export const FREEZE_STATUS_TONE: Record<FreezeStatus, Tone> = {
  requested: "neutral",
  active: "accent",
  finished: "neutral",
  rejected: "danger",
  lifted: "warning",
};

export const PAYMENT_AGREEMENT_STATUS_LABEL: Record<PaymentAgreementStatus, string> = {
  active: "Vigente",
  completed: "Cumplido",
  defaulted: "Caído",
};

export const PAYMENT_AGREEMENT_STATUS_TONE: Record<PaymentAgreementStatus, Tone> = {
  active: "accent",
  completed: "positive",
  defaulted: "danger",
};

export const CARD_BRAND_LABEL: Record<CardBrand, string> = {
  visa: "Visa",
  mastercard: "Mastercard",
  other: "Otra",
};

export const DEBIT_AUTHORIZATION_STATUS_LABEL: Record<DebitAuthorizationStatus, string> = {
  active: "Adherido",
  scheduled_cancel: "Baja programada",
  canceled: "Dado de baja",
  suspended: "Suspendida por rechazos",
};

export const DEBIT_AUTHORIZATION_STATUS_TONE: Record<DebitAuthorizationStatus, Tone> = {
  active: "positive",
  scheduled_cancel: "warning",
  canceled: "neutral",
  suspended: "danger",
};

/** `debitAttempts.result`; `null` = todavía no se ejecutó (no es «rechazado»). */
export const DEBIT_ATTEMPT_RESULT_LABEL: Record<string, string> = {
  approved: "Aprobado",
  rejected: "Rechazado",
  pending: "Programado",
};

export const DEBIT_ATTEMPT_RESULT_TONE: Record<string, Tone> = {
  approved: "positive",
  rejected: "danger",
  pending: "neutral",
};

/** RN-CAJ-02: los únicos medios de pago del sistema. */
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: "Efectivo",
  payway_terminal: "Posnet Payway",
  payway_recurring: "Débito automático",
  payway_online: "Pago online",
  transfer: "Transferencia",
  mp_point: "MP Point",
  mp_qr: "QR de Mercado Pago",
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  confirmed: "Confirmado",
  pending_reconciliation: "Pendiente de conciliación",
  observed: "Observado",
  reversed: "Revertido",
};

export const PAYMENT_STATUS_TONE: Record<PaymentStatus, Tone> = {
  confirmed: "positive",
  pending_reconciliation: "warning",
  observed: "warning",
  reversed: "danger",
};

export function labelFor(map: Record<string, string>, key: string | null | undefined): string {
  if (!key) return "—";
  return map[key] ?? key;
}

/** Igual que `labelFor` pero para tonos: un estado que el front no conoce se pinta neutro. */
export function toneFor(map: Record<string, Tone>, key: string | null | undefined): Tone {
  if (!key) return "neutral";
  return map[key] ?? "neutral";
}

// ─────────────────────────────────────────────────────────────────────────────
// §3 Socios y membresías — enums de `@viva/shared` en español rioplatense
// ─────────────────────────────────────────────────────────────────────────────

/** RN-SOC-28: los cuatro estados que el job diario calcula, más el archivo migrado. */
export const MEMBER_STATUS_LABEL: Record<MemberStatus, string> = {
  active: "Activo",
  delinquent: "Moroso",
  inactive: "Inactivo",
  terminated: "De baja",
  archived: "Archivado",
};

export const MEMBER_STATUS_TONE: Record<MemberStatus, Tone> = {
  active: "positive",
  delinquent: "warning",
  inactive: "neutral",
  terminated: "danger",
  archived: "neutral",
};

/** Qué significa cada estado, con su regla. Se muestra en el filtro del padrón. */
export const MEMBER_STATUS_HINT: Record<MemberStatus, string> = {
  active: "Membresía vigente, o vencida dentro de la tolerancia.",
  delinquent: "Cuota vencida más allá de la tolerancia y hasta 2 meses.",
  inactive: "2 o más meses desde el último vencimiento sin renovar.",
  terminated: "Baja explícita registrada con motivo.",
  archived: "Ficha absorbida por una fusión o archivo histórico migrado.",
};

/** RN-SOC-11/13/14 — `getAptStatus`. `none` = nunca cargó apto, no es «vencido». */
export const APT_STATE_LABEL: Record<AptState, string> = {
  none: "Sin apto",
  pending_validation: "Pendiente de validar",
  valid: "Vigente",
  expiring: "Por vencer",
  in_grace: "En gracia",
  expired: "Vencido",
};

export const APT_STATE_TONE: Record<AptState, Tone> = {
  none: "danger",
  pending_validation: "info",
  valid: "positive",
  expiring: "warning",
  in_grace: "warning",
  expired: "danger",
};

export const MEDICAL_CERTIFICATE_STATUS_LABEL: Record<MedicalCertificateStatus, string> = {
  pending_validation: "Pendiente de validar",
  valid: "Válido",
  expired: "Vencido",
};

export const MEDICAL_CERTIFICATE_STATUS_TONE: Record<MedicalCertificateStatus, Tone> = {
  pending_validation: "info",
  valid: "positive",
  expired: "danger",
};

/** RN-SOC-07: lo que le falta a la ficha y recepción persigue. */
export const INCOMPLETE_FLAG_LABEL: Record<IncompleteFlag, string> = {
  address: "Sin dirección",
  apt: "Sin apto vigente",
  emergency_contact: "Sin contacto de emergencia",
  email: "Sin mail",
  fingerprint: "Sin huella",
};

export const DOC_TYPE_LABEL: Record<DocType, string> = {
  dni: "DNI",
  passport: "Pasaporte",
  foreign_id: "Doc. del país de origen",
};

/** RN-SOC-36: hoy son sólo estas dos plataformas (rel. 4.1). */
export const PLATFORM_LABEL: Record<Platform, string> = {
  sportclub: "SportClub PLAN TOTAL",
  wellhub: "Wellhub",
};

/** RN-PLA-36: a qué visitantes se vende un complemento de plataforma (Infinity). */
export const PLAN_PLATFORM_REQUIREMENT_LABEL: Record<PlanPlatformRequirement, string> = {
  sportclub: "Visitantes de SportClub",
  wellhub: "Visitantes de Wellhub",
  any: "Visitantes de SportClub o Wellhub",
};

export const VISITOR_STATUS_LABEL: Record<VisitorStatus, string> = {
  active: "Activo",
  blocked: "Bloqueado",
  converted: "Convertido en socio",
};

export const VISITOR_STATUS_TONE: Record<VisitorStatus, Tone> = {
  active: "positive",
  blocked: "danger",
  converted: "accent",
};

/** RN-SOC-40: `missing` = nunca se verificó; no es lo mismo que vencida. */
export const VERIFICATION_STATE_LABEL: Record<"valid" | "expired" | "missing", string> = {
  valid: "Verificado",
  expired: "Verificación vencida",
  missing: "Sin verificar",
};

export const VERIFICATION_STATE_TONE: Record<"valid" | "expired" | "missing", Tone> = {
  valid: "positive",
  expired: "danger",
  missing: "warning",
};

/** RN-SOC-22/23: cortesía, descuento personal o canje. */
export const BENEFIT_KIND_LABEL: Record<BenefitKind, string> = {
  courtesy_100: "Cortesía (sin cargo)",
  permanent_discount: "Descuento personal",
  barter: "Canje",
};

export const BENEFIT_KIND_TONE: Record<BenefitKind, Tone> = {
  courtesy_100: "accent",
  permanent_discount: "info",
  barter: "warning",
};

export const BENEFIT_STATUS_LABEL: Record<BenefitStatus, string> = {
  active: "Vigente",
  revoked: "Revocado",
};

export const BENEFIT_STATUS_TONE: Record<BenefitStatus, Tone> = {
  active: "positive",
  revoked: "neutral",
};

/** RN-PLA-20 — crédito por referir: se usa una sola vez, en la próxima cuota, o vence. */
export const REFERRAL_CREDIT_STATUS_LABEL: Record<ReferralCreditStatus, string> = {
  available: "Disponible",
  used: "Usado",
  expired: "Vencido",
};

export const REFERRAL_CREDIT_STATUS_TONE: Record<ReferralCreditStatus, Tone> = {
  available: "positive",
  used: "neutral",
  expired: "neutral",
};

/** RN-SOC-21: descuento sobre lista o cupo facturado a la empresa. */
export const CORPORATE_CONDITION_LABEL: Record<CorporateConditionKind, string> = {
  discount_pct: "Descuento % sobre lista",
  company_billed: "Cupo facturado a la empresa",
};

export const CORPORATE_AGREEMENT_STATUS_LABEL: Record<CorporateAgreementStatus, string> = {
  active: "Vigente",
  expired: "Vencido",
  terminated: "Rescindido",
};

export const CORPORATE_AGREEMENT_STATUS_TONE: Record<CorporateAgreementStatus, Tone> = {
  active: "positive",
  expired: "warning",
  terminated: "danger",
};

export const FAMILY_GROUP_STATUS_LABEL: Record<FamilyGroupStatus, string> = {
  active: "Vigente",
  dissolved: "Disuelto",
};

export const FAMILY_GROUP_STATUS_TONE: Record<FamilyGroupStatus, Tone> = {
  active: "positive",
  dissolved: "neutral",
};

/** RN-SOC-24: los textos legales versionados. */
export const LEGAL_DOC_TYPE_LABEL: Record<LegalDocType, string> = {
  rules: "Reglamento y consideraciones generales",
  image_consent: "Cesión de imagen",
  minor_withdrawal: "Retiro de menores",
  debit_consent: "Autorización de débito automático",
  health_declaration_form: "Declaración de condición física",
  trial_terms: "Términos de la clase de prueba",
};

/** Qué opción SI/NO guarda cada texto (lo que la tablet muestra al firmar). */
export const LEGAL_DOC_CHOICE_LABEL: Partial<Record<LegalDocType, { yes: string; no: string }>> = {
  image_consent: { yes: "AUTORIZO el uso de mi imagen", no: "NO AUTORIZO el uso de mi imagen" },
  minor_withdrawal: {
    yes: "AUTORIZO que se retire por sus propios medios",
    no: "NO AUTORIZO que se retire por sus propios medios",
  },
};

export const SIGNATURE_MEDIUM_LABEL: Record<SignatureMedium, string> = {
  app_authenticated: "App con usuario autenticado",
  kiosk_signature: "Firma manuscrita en la tablet",
};

export const SIGNER_KIND_LABEL: Record<SignerKind, string> = {
  member: "El socio",
  visitor: "El visitante",
  guardian: "El adulto responsable",
  third_party: "Un tercero",
};

/** RN-SOC-42: cómo se retiró la persona. */
export const WITHDRAWAL_MODE_LABEL: Record<WithdrawalMode, string> = {
  emergency_service: "Servicio de emergencias",
  own_means: "Por sus propios medios",
  family_third: "Familiar o tercero",
};

/** RN-SOC-43: por qué dos fichas son candidatas a duplicado. */
export const MERGE_REASON_LABEL: Record<"same_document" | "same_name_and_birth", string> = {
  same_document: "Mismo documento",
  same_name_and_birth: "Mismo nombre y fecha de nacimiento",
};

// Lo que la ficha 360° muestra de §4 sin interpretarlo (docs/api/socios.md §14).

// §8 Caja, control del dinero y conciliación
// ─────────────────────────────────────────────────────────────────────────────

/** Los que se cobran en el mostrador (los otros no los recibe una persona). */
export const COUNTER_PAYMENT_METHODS: PaymentMethod[] = ["cash", "payway_terminal", "transfer"];

export const CASH_SHIFT_SLOT_LABEL: Record<CashShiftSlot, string> = {
  morning: "Mañana",
  afternoon: "Tarde",
  single: "Turno único",
};

export const CASH_SHIFT_STATUS_LABEL: Record<CashShiftStatus, string> = {
  open: "Abierto",
  closed: "Pendiente de revisión",
  reviewed: "Revisado",
};

export const CASH_SHIFT_STATUS_TONE: Record<CashShiftStatus, Tone> = {
  open: "accent",
  closed: "warning",
  reviewed: "positive",
};

/** RN-CAJ-08/15/22: el libro de caja, con signo. */
export const CASH_MOVEMENT_KIND_LABEL: Record<CashMovementKind, string> = {
  cash_in: "Cobro en efectivo",
  petty_expense: "Gasto menor",
  envelope_out: "Sobre preparado",
  refund_out: "Devolución en efectivo",
  adjustment: "Ajuste",
  kiosk_purchase: "Compra a proveedor",
};

export const CASH_DIFFERENCE_KIND_LABEL: Record<CashDifferenceKind, string> = {
  opening: "Apertura",
  closing: "Cierre",
  audit: "Arqueo",
  pos_batch: "Lote de posnet",
  double_charge: "Doble cobro",
};

export const CASH_DIFFERENCE_STATUS_LABEL: Record<CashDifferenceStatus, string> = {
  unjustified: "Sin justificar",
  justified: "Justificada",
  deducted: "Descontada",
  assumed_by_house: "Asumida por la casa",
};

export const CASH_DIFFERENCE_STATUS_TONE: Record<CashDifferenceStatus, Tone> = {
  unjustified: "danger",
  justified: "positive",
  deducted: "warning",
  assumed_by_house: "info",
};

export const ENVELOPE_STATUS_LABEL: Record<EnvelopeStatus, string> = {
  in_branch: "En sede",
  withdrawn: "Retirado",
};

export const ENVELOPE_STATUS_TONE: Record<EnvelopeStatus, Tone> = {
  in_branch: "warning",
  withdrawn: "positive",
};

export const POS_BATCH_STATUS_LABEL: Record<PosBatchStatus, string> = {
  open: "Abierto",
  reconciled: "Conciliado",
  with_differences: "Con diferencias",
};

export const POS_BATCH_STATUS_TONE: Record<PosBatchStatus, Tone> = {
  open: "neutral",
  reconciled: "positive",
  with_differences: "danger",
};

export const BANK_FEED_MATCH_STATUS_LABEL: Record<BankFeedMatchStatus, string> = {
  unmatched: "Sin conciliar",
  proposed: "Match propuesto",
  matched: "Conciliado",
  ignored: "Ignorado",
};

export const REVERSAL_KIND_LABEL: Record<ReversalKind, string> = {
  void: "Anulación",
  refund: "Devolución",
  correction: "Corrección",
};

export const CHARGE_STATE_LABEL: Record<"pending" | "overdue" | "in_collection", string> = {
  pending: "A vencer",
  overdue: "Vencida",
  in_collection: "En gestión de cobro",
};

export const CHARGE_STATE_TONE: Record<"pending" | "overdue" | "in_collection", Tone> = {
  pending: "neutral",
  overdue: "danger",
  in_collection: "warning",
};

// ─────────────────────────────────────────────────────────────────────────────
// §6 Acceso físico e ingreso (RN-ACC-04/08/15/21)
// ─────────────────────────────────────────────────────────────────────────────

/** RN-ACC-04: cada ingreso registra CON QUÉ se identificó la persona. */
export const ACCESS_METHOD_LABEL: Record<AccessMethod, string> = {
  fingerprint: "Huella",
  member_qr: "QR del socio",
  location_qr: "QR del local",
  manual: "Habilitación manual",
  agreement: "Convenio",
};

/** RN-ACC-08: el semáforo. «Permitido con aviso» NO es «permitido»: es el amarillo. */
export const ACCESS_RESULT_LABEL: Record<AccessResult, string> = {
  allowed: "Permitido",
  allowed_warning: "Permitido con aviso",
  rejected: "Rechazado",
};

export const ACCESS_RESULT_TONE: Record<AccessResult, Tone> = {
  allowed: "positive",
  allowed_warning: "warning",
  rejected: "danger",
};

/** El color del semáforo que corresponde a cada resultado del registro. */
export const ACCESS_RESULT_LIGHT: Record<AccessResult, "green" | "yellow" | "red"> = {
  allowed: "green",
  allowed_warning: "yellow",
  rejected: "red",
};

export const ACCESS_PERSON_KIND_LABEL: Record<AccessPersonKind, string> = {
  member: "Socio",
  visitor: "Visitante por convenio",
  trial: "Clase de prueba",
  staff: "Personal",
  unknown: "No identificada",
};

/**
 * Motivo del rechazo o del aviso (RN-ACC-08..11, RN-ACC-19).
 *
 * El backend manda además `reason` con el texto del AF; estos rótulos son el respaldo
 * cuando sólo viaja el código (el registro histórico, por ejemplo) y el nombre del filtro.
 */
export const ACCESS_REASON_LABEL: Record<AccessReason, string> = {
  debt: "Cuota vencida",
  medical_certificate_expired: "Apto médico vencido",
  medical_certificate_grace: "Apto médico en gracia",
  membership_frozen: "Plan congelado",
  membership_expired: "Plan vencido",
  plan_without_access: "Plan sin acceso",
  weekly_limit_reached: "Frecuencia semanal agotada",
  agreement_not_valid: "Convenio no vigente",
  qr_already_used: "QR ya utilizado",
  qr_expired: "QR vencido",
  location_closed: "Local cerrado",
  admission_ban: "Derecho de admisión",
  antipassback: "QR ya utilizado (antipassback)",
  unknown_person: "Persona no identificada",
  person_inactive: "Ficha sin derecho de ingreso",
};

/** Badges del semáforo (AF §6). `courtesy_pct:<n>` es el único paramétrico. */
export function accessBadgeLabel(badge: string): string {
  if (badge.startsWith("courtesy_pct:")) {
    const pct = badge.slice("courtesy_pct:".length);
    return `Cortesía ${pct} %`;
  }
  const map: Record<string, string> = {
    minor_authorized: "Menor · retiro AUTORIZADO",
    minor_not_authorized: "Menor · retiro NO AUTORIZADO",
    visitor_sportclub: "Convenio SportClub",
    visitor_wellhub: "Convenio Wellhub",
    trial: "Clase de prueba",
    health_flag: "Declaración de salud marcada",
    dni_only: "Ingreso por DNI",
  };
  return map[badge] ?? badge;
}

/** El badge de un menor sin autorización de retiro no bloquea, pero se mira distinto. */
export function accessBadgeTone(badge: string): Tone {
  if (badge === "minor_not_authorized") return "danger";
  if (badge === "health_flag" || badge === "minor_authorized") return "warning";
  if (badge.startsWith("courtesy_pct:")) return "accent";
  if (badge === "visitor_sportclub" || badge === "visitor_wellhub") return "info";
  return "neutral";
}

/** RN-ACC-05: los 10 dedos se pueden registrar. */
export const FINGER_LABEL: Record<Finger, string> = {
  right_thumb: "Pulgar derecho",
  right_index: "Índice derecho",
  right_middle: "Mayor derecho",
  right_ring: "Anular derecho",
  right_little: "Meñique derecho",
  left_thumb: "Pulgar izquierdo",
  left_index: "Índice izquierdo",
  left_middle: "Mayor izquierdo",
  left_ring: "Anular izquierdo",
  left_little: "Meñique izquierdo",
};

export const TIMECLOCK_DIRECTION_LABEL: Record<TimeclockDirection, string> = {
  in: "Entrada",
  out: "Salida",
};

export const TIMECLOCK_METHOD_LABEL: Record<TimeclockMethod, string> = {
  fingerprint: "Huella en el molinete",
  pin: "PIN en la tablet",
  personal_qr: "QR personal",
  mobile: "Celular (local sin molinete)",
};

// ─────────────────────────────────────────────────────────────────────────────
// §10 CRM y comunicación
// ─────────────────────────────────────────────────────────────────────────────

/** RN-CRM-03: el pipeline del interesado. `converted` no retrocede. */
export const PROSPECT_STATUS_LABEL: Record<ProspectStatus, string> = {
  new: "Nuevo",
  following: "En seguimiento",
  trial_scheduled: "Prueba agendada",
  converted: "Convertido",
  lost: "Perdido",
};

export const PROSPECT_STATUS_TONE: Record<ProspectStatus, Tone> = {
  new: "info",
  following: "accent",
  trial_scheduled: "warning",
  converted: "positive",
  lost: "danger",
};

export const PROSPECT_STATUS_HINT: Record<ProspectStatus, string> = {
  new: "Todavía no lo contactó nadie.",
  following: "Alguien lo está siguiendo: hay contactos o tareas abiertas.",
  trial_scheduled: "Tiene la clase de prueba agendada en la grilla real.",
  converted: "Ya es socio. De acá no se vuelve atrás.",
  lost: "Se cerró con un motivo del catálogo.",
};

/**
 * RN-CRM-02: el origen es obligatorio y sale del catálogo `contact_sources`, que es
 * ADMINISTRABLE. Este mapa sólo traduce los códigos que el catálogo trae de fábrica: un
 * código nuevo se muestra tal cual (`labelFor`), nunca como «otro».
 */
export const CONTACT_SOURCE_LABEL: Record<string, string> = {
  instagram: "Instagram",
  referral: "Recomendación de socio",
  google_ads: "Campaña de Google",
  walk_in: "Cartel / caminante",
  other: "Otro",
};

/** RN-CRM-03: catálogo `lost_reasons`, también administrable. */
export const LOST_REASON_LABEL: Record<string, string> = {
  price: "Precio",
  schedule: "Horario",
  distance: "Distancia",
  no_answer: "No contesta",
  spam: "Spam",
  other: "Otro",
};

/** Canal de un contacto humano registrado a mano (RN-CRM-07/18). */
export const CONTACT_CHANNEL_LABEL: Record<string, string> = {
  call: "Llamada",
  whatsapp_externo: "WhatsApp (fuera del sistema)",
  whatsapp: "WhatsApp (fuera del sistema)",
  email: "Email",
  presencial: "Presencial",
  instagram: "Instagram",
  tarea: "Cierre de tarea",
  campaign: "Campaña",
  otro: "Otro",
};

export const CONTACT_PERSON_KIND_LABEL: Record<ContactPersonKind, string> = {
  member: "Socio",
  prospect: "Interesado",
};

/** RN-CRM-08: los seis segmentos del AF. El backend manda su `label`; esto es el respaldo. */
export const SEGMENT_LABEL: Record<RetentionSegment, string> = {
  absent_14d: "Ausentes 14 días o más",
  overdue: "Cuota vencida",
  plan_expiring: "Plan por vencer",
  birthday_month: "Cumpleaños del mes",
  former_members: "Ex-socios",
  lost_prospects: "Interesados perdidos",
};

export const CAMPAIGN_STATUS_LABEL: Record<CampaignStatus, string> = {
  draft: "Borrador",
  scheduled: "Programada",
  sent: "Enviada",
  canceled: "Cancelada",
};

export const CAMPAIGN_STATUS_TONE: Record<CampaignStatus, Tone> = {
  draft: "neutral",
  scheduled: "info",
  sent: "positive",
  canceled: "danger",
};

export const NOTIFICATION_CHANNEL_LABEL: Record<NotificationChannel, string> = {
  push: "Push",
  email: "Email",
  inapp: "Centro de avisos",
};

/** RN-CRM-13/14: estado de una publicación del muro. */
export const POST_STATUS_LABEL: Record<PostStatus, string> = {
  pending: "Pendiente de moderación",
  approved: "Aprobada",
  rejected: "Rechazada",
  hidden: "Oculta",
  removed: "Eliminada",
  deleted: "Borrada por su autor",
  draft: "Borrador",
  unpublished: "Despublicada",
};

export const POST_STATUS_TONE: Record<PostStatus, Tone> = {
  pending: "warning",
  approved: "positive",
  rejected: "danger",
  hidden: "neutral",
  removed: "danger",
  deleted: "neutral",
  draft: "neutral",
  unpublished: "neutral",
};

export const MODERATION_ACTION_LABEL: Record<ModerationAction, string> = {
  approve: "Aprobar",
  reject: "Rechazar",
  hide: "Ocultar",
  remove: "Eliminar",
  restore: "Restituir",
};

/** RN-CRM-16: la bandeja ordena por «sin responder» más antiguo. */
export const CONVERSATION_STATUS_LABEL: Record<ConversationStatus, string> = {
  unanswered: "Sin responder",
  answered: "Respondida",
  closed: "Cerrada",
};

export const CONVERSATION_STATUS_TONE: Record<ConversationStatus, Tone> = {
  unanswered: "danger",
  answered: "positive",
  closed: "neutral",
};

/** RN-CRM-18: de dónde sale cada línea de la línea de tiempo. */
export const COMMUNICATION_SOURCE_LABEL: Record<CommunicationSource, string> = {
  notification: "Aviso automático",
  campaign: "Campaña",
  conversation: "Conversación",
  contact_log: "Contacto registrado",
  task: "Tarea",
};

export const COMMUNICATION_SOURCE_TONE: Record<CommunicationSource, Tone> = {
  notification: "info",
  campaign: "accent",
  conversation: "positive",
  contact_log: "neutral",
  task: "warning",
};

/** Los 9 avisos del catálogo (RN-CRM-10) más los que agregan otros módulos. */
export const NOTIFICATION_TYPE_LABEL: Record<string, string> = {
  due_soon: "Cuota por vencer",
  debt: "Deuda / cuota vencida",
  birthday: "Cumpleaños",
  class_canceled: "Clase cancelada",
  spot_available: "Lugar liberado",
  absent_14d: "Dos semanas sin venir",
  trial_scheduled: "Clase de prueba agendada",
  apt_expiring: "Apto médico por vencer",
  waitlist_reminder: "Confirmación de lista de espera",
  message_reply: "Respuesta de la sede",
  campaign: "Campaña",
  security_alert: "Alerta de seguridad",
  business_alert: "Alerta de negocio",
  cash_close: "Cierre de caja",
  daily_digest: "Resumen del día",
  teacher_absence: "Ausencia de profesor",
  personal_training_lost: "Personal training dado de baja",
  class_pending_confirm: "Clase sin alumnos a confirmar",
  payroll_ready: "Pre-liquidación lista",
};

// ── §12 Reportes y alertas · §9.2 Resultado por sede y actividad ────────────
//
// Los enums viven en `@viva/shared` (`schemas/reportes.ts`), igual que el texto de
// `REVENUE_CONCEPT_LABELS` y `ATTRIBUTION_BASIS_LABELS`, que el backend usa para armar el
// `basisLabel` de cada cifra (RN-CON-18bis). Acá va sólo lo que es de pantalla.

export const BUSINESS_ALERT_KIND_LABEL: Record<BusinessAlertKind, string> = {
  attendance_drop: "Caída de asistencia",
  revenue_drop: "Caída de facturación",
};

export const BUSINESS_ALERT_KIND_TONE: Record<BusinessAlertKind, Tone> = {
  attendance_drop: "warning",
  revenue_drop: "danger",
};

/** RN-CON-17 / PV-73: cómo se prorratean los gastos de la sede virtual Central. */
export const CENTRAL_ALLOCATION_MODE_LABEL: Record<CentralAllocationMode, string> = {
  by_revenue: "Proporcional a la facturación del mes",
  equal: "Partes iguales",
  manual: "Pesos manuales por sede",
};

export const CENTRAL_ALLOCATION_MODE_HINT: Record<CentralAllocationMode, string> = {
  by_revenue:
    "Es el valor por defecto. Si todas las sedes facturaron cero, cae a partes iguales y queda marcado.",
  equal: "Cada sede se lleva la misma porción de Central, facture lo que facture.",
  manual: "Los pesos que cargues se normalizan al 100 %.",
};

/** PV-109: qué se hace con los costos comunes en la vista por actividad. */
export const COMMON_COSTS_ALLOCATION_LABEL: Record<"none" | "by_present" | "by_revenue", string> = {
  none: "No se reparten (valor por defecto)",
  by_present: "Repartir por presentes",
  by_revenue: "Repartir por ingreso atribuido",
};

export const COMMON_COSTS_ALLOCATION_HINT: Record<"none" | "by_present" | "by_revenue", string> = {
  none: "Cada actividad muestra su margen de contribución y los comunes se informan aparte.",
  by_present: "Se reparte el común de la sede en proporción a los presentes de cada actividad.",
  by_revenue: "Se reparte el común de la sede en proporción al ingreso atribuido a cada actividad.",
};

/** RN-REP-08: por qué un socio está en la lista. Puede estar por las dos. */
export const AT_RISK_REASON_LABEL: Record<"no_attendance" | "overdue_charge", string> = {
  no_attendance: "Sin venir",
  overdue_charge: "Cuota vencida",
};

export const AT_RISK_REASON_TONE: Record<"no_attendance" | "overdue_charge", Tone> = {
  no_attendance: "warning",
  overdue_charge: "danger",
};

export const REPORT_EXPORT_FORMAT_LABEL: Record<ReportExportFormat, string> = {
  csv: "CSV (Excel es-AR)",
  xlsx: "Excel (.xlsx)",
  pdf: "PDF",
};

/** Arquitectura §11: de dónde salió la fila del cache de agregados. */
export const AGGREGATE_SOURCE_LABEL: Record<string, string> = {
  nightly: "Job nocturno",
  shift_close: "Cierre de caja",
  manual: "Recálculo manual",
  live: "En vivo",
};

export const REPORT_METRIC_LABEL: Record<ReportMetric, string> = {
  revenue: "Facturación",
  attendance: "Asistencia",
  members_active: "Socios activos",
};

// ── §13 Migración y puesta en marcha ────────────────────────────────────────

export const MIGRATION_RUN_KIND_LABEL: Record<MigrationRunKind, string> = {
  test: "Prueba",
  delta: "Delta",
  final: "Delta final del corte",
};

export const MIGRATION_RUN_KIND_HINT: Record<MigrationRunKind, string> = {
  test: "Corrida completa de ensayo. Caduca a los 30 días: los datos viejos siguen moviéndose.",
  delta: "Importa sólo lo que cambió desde la corrida anterior.",
  final: "La corrida del corte. Sólo el Dueño, con motivo, y no se deshace.",
};

export const MIGRATION_RUN_KIND_TONE: Record<MigrationRunKind, Tone> = {
  test: "neutral",
  delta: "info",
  final: "accent",
};

export const MIGRATION_RUN_STATUS_LABEL: Record<MigrationRunStatus, string> = {
  running: "Corriendo",
  completed: "Completada",
  failed: "Fallida",
};

export const MIGRATION_RUN_STATUS_TONE: Record<MigrationRunStatus, Tone> = {
  running: "warning",
  completed: "positive",
  failed: "danger",
};

/** Los adaptadores de importación (no son «sistemas de origen»: ver `MIGRATION_ADAPTERS`). */
export const MIGRATION_ADAPTER_LABEL: Record<MigrationAdapter, string> = {
  crossfy: "Crossfy (Excel del padrón)",
  controlgym: "Control Gym (txt de accesos)",
  "drive-prospects": "Drive de averiguaciones (Excel)",
  "schedules-xlsx": "Grillas de clases por sede (Excel)",
};

export const LAUNCH_CHECKLIST_KIND_LABEL: Record<LaunchChecklistKind, string> = {
  initial_load: "Carga inicial",
  validation: "Validaciones",
  module_approval: "Aprobación de módulos",
  training: "Capacitación",
  decommission: "Bajas del mundo viejo",
};

export const LAUNCH_CHECKLIST_KIND_HINT: Record<LaunchChecklistKind, string> = {
  initial_load: "Lo que hay que tener cargado y verificado antes del corte.",
  validation:
    "Las comprobaciones sobre los datos migrados y lo que Vero firma antes de agendar el corte.",
  module_approval: "Cada parte del sistema la prueban y aprueban el dueño Y Vero.",
  training: "Capacitación por rol antes de largar el sistema a los socios.",
  decommission:
    "Son POSTERIORES al corte: no lo frenan, pero sin la lista nadie se acuerda de apagarlas.",
};

export const LAUNCH_CHECKLIST_STATUS_LABEL: Record<LaunchChecklistStatus, string> = {
  pending: "Pendiente",
  loaded: "Cargado",
  verified: "Verificado",
  approved: "Aprobado",
};

export const LAUNCH_CHECKLIST_STATUS_TONE: Record<LaunchChecklistStatus, Tone> = {
  pending: "danger",
  loaded: "warning",
  verified: "positive",
  approved: "accent",
};

export const LAUNCH_MODULE_LABEL: Record<LaunchModule, string> = {
  socios: "Socios y membresías",
  "planes-cobranzas": "Planes y cobranzas",
  caja: "Caja y control del dinero",
  contable: "Contable y facturación ARCA",
  clases: "Clases y reservas",
  acceso: "Acceso físico y molinetes",
  profesores: "Profesores y liquidación",
  crm: "CRM y comunicación",
  kiosko: "Kiosko y stock",
  reportes: "Reportes y alertas",
};

/** RN-MIG-11: qué se prende sede por sede. La **caja no está acá**: no se escalona. */
export const LIVE_FEATURE_LABEL: Record<"access" | "bookings" | "kiosk", string> = {
  access: "Acceso (molinete / QR)",
  bookings: "Reservas por app",
  kiosk: "Kiosko",
};

export const LIVE_FEATURE_HINT: Record<"access" | "bookings" | "kiosk", string> = {
  access:
    "Apagado, el molinete y el QR no operan: recepción controla el ingreso mirando el estado del socio en pantalla.",
  bookings: "Apagado, el socio no reserva desde la app en esta sede.",
  kiosk: "Apagado, la tablet de esta recepción no vende ni toma firmas.",
};
// §5 Clases, grilla, reservas y asistencia
// ─────────────────────────────────────────────────────────────────────────────

export const SCHEDULE_STATUS_LABEL: Record<ScheduleStatus, string> = {
  draft: "Borrador",
  published: "Publicada",
};

export const SCHEDULE_STATUS_TONE: Record<ScheduleStatus, Tone> = {
  draft: "warning",
  published: "positive",
};

export const SCHEDULED_CLASS_STATUS_LABEL: Record<ScheduledClassStatus, string> = {
  active: "Activa",
  removed: "Dada de baja",
};

export const CLASS_SESSION_STATUS_LABEL: Record<ClassSessionStatus, string> = {
  scheduled: "Programada",
  canceled: "Cancelada",
  consolidated: "Consolidada",
  pending_confirm: "Sin presentes, a confirmar",
  confirmed_no_students: "Dictada sin alumnos",
};

export const CLASS_SESSION_STATUS_TONE: Record<ClassSessionStatus, Tone> = {
  scheduled: "neutral",
  canceled: "danger",
  consolidated: "positive",
  pending_confirm: "warning",
  confirmed_no_students: "info",
};

export const BOOKING_STATUS_LABEL: Record<BookingStatus, string> = {
  booked: "Reservada",
  canceled_in_time: "Cancelada en término",
  canceled_late: "Cancelada tarde",
  present: "Presente",
  absent: "Ausente",
};

export const BOOKING_STATUS_TONE: Record<BookingStatus, Tone> = {
  booked: "neutral",
  canceled_in_time: "info",
  canceled_late: "warning",
  present: "positive",
  absent: "danger",
};

/** De dónde salió la reserva (RN-CLA-15/21/32). */
export const BOOKING_ORIGIN_LABEL: Record<BookingOrigin, string> = {
  app: "Por la app",
  reception: "Recepción",
  fixed: "Fija",
  waitlist: "Lista de espera",
  walkin: "Walk-in",
};

export const BOOKING_PERSON_KIND_LABEL: Record<BookingPersonKind, string> = {
  member: "Socio",
  visitor: "Visitante",
  trial: "Clase de prueba",
};

export const CHECKIN_METHOD_LABEL: Record<CheckinMethod, string> = {
  room_qr: "QR del salón",
  teacher_manual: "Marcado por el profe",
  coordinator_fix: "Corrección del coordinador",
};

export const FIXED_SLOT_STATUS_LABEL: Record<FixedSlotStatus, string> = {
  requested: "Solicitud pendiente",
  active: "Fijo vigente",
  suspended: "Suspendida",
  dropped: "Caída",
};

export const FIXED_SLOT_STATUS_TONE: Record<FixedSlotStatus, Tone> = {
  requested: "warning",
  active: "positive",
  suspended: "info",
  dropped: "danger",
};

export const WAITLIST_STATUS_LABEL: Record<WaitlistStatus, string> = {
  waiting: "En espera",
  offered: "Lugar ofrecido",
  confirmed: "Confirmó",
  declined: "Rechazó",
  expired: "Venció la ventana",
  closed: "Cerrada",
};

export const WAITLIST_STATUS_TONE: Record<WaitlistStatus, Tone> = {
  waiting: "neutral",
  offered: "accent",
  confirmed: "positive",
  declined: "warning",
  expired: "warning",
  closed: "neutral",
};

export const PENALTY_KIND_LABEL: Record<PenaltyKind, string> = {
  booking_restriction: "Sin reserva anticipada",
  fixed_slot_block: "Bloqueo de fijación",
};

export const RECOVERY_CREDIT_ORIGIN_LABEL: Record<RecoveryCreditOrigin, string> = {
  canceled_in_time: "Canceló en término",
  club_canceled: "La canceló el club",
  holiday: "Feriado",
  manual: "Compensación manual",
};

export const EVENT_KIND_LABEL: Record<EventKind, string> = {
  master: "Clase master",
  viva_games: "Viva Games",
};

export const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  draft: "Borrador",
  published: "Publicado",
  closed: "Cerrado",
  canceled: "Cancelado",
};

export const EVENT_STATUS_TONE: Record<EventStatus, Tone> = {
  draft: "warning",
  published: "positive",
  closed: "neutral",
  canceled: "danger",
};

export const EVENT_REGISTRATION_STATUS_LABEL: Record<EventRegistrationStatus, string> = {
  pending_payment: "Pendiente de pago",
  confirmed: "Confirmada",
  refunded: "Devuelta",
  expired: "Vencida sin pagar",
};

export const EVENT_REGISTRATION_STATUS_TONE: Record<EventRegistrationStatus, Tone> = {
  pending_payment: "warning",
  confirmed: "positive",
  refunded: "info",
  expired: "neutral",
};

/** Domingo primero: `weekday` de `scheduledClasses` es 0..6 con 0 = domingo. */
export const WEEKDAY_LABEL = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
] as const;

export const WEEKDAY_SHORT = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"] as const;

/** Orden de la grilla: la semana operativa arranca el lunes (`week.start`, RN-CLA-23). */
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;
// §7 Profesores, liquidación y comisiones
// ─────────────────────────────────────────────────────────────────────────────

/** RN-PRO-01: los cinco tipos de legajo del AF, más los dos de coordinación. */
export const STAFF_TYPE_LABEL: Record<StaffType, string> = {
  sala_teacher: "Profe de sala",
  class_teacher: "Profe de clase",
  receptionist: "Recepcionista",
  branch_manager: "Encargado de sede",
  admin: "Administración",
  intersede_coordinator: "Coordinación intersedes",
};

/** RN-PRO-30: dependencia (el sistema NO emite recibos) o externo que factura. */
export const HIRE_MODE_LABEL: Record<HireMode, string> = {
  payroll: "Relación de dependencia",
  external_billing: "Externo que factura",
};

export const HIRE_MODE_SHORT: Record<HireMode, string> = {
  payroll: "Dependencia",
  external_billing: "Externo",
};

export const HIRE_MODE_TONE: Record<HireMode, Tone> = {
  payroll: "neutral",
  external_billing: "info",
};

export const STAFF_STATUS_LABEL: Record<StaffStatus, string> = {
  active: "Activo",
  inactive: "Inactivo",
};

export const STAFF_STATUS_TONE: Record<StaffStatus, Tone> = {
  active: "positive",
  inactive: "neutral",
};

/** RN-PRO-07: la grilla de turnos modela sala y recepción, nada más. */
export const WORK_SHIFT_KIND_LABEL: Record<WorkShiftKind, string> = {
  sala: "Sala",
  reception: "Recepción",
};

/** RN-PRO-08: las discrepancias del cruce fichada × turno, con lo que se computa. */
export const TIMECLOCK_KIND_LABEL: Record<TimeclockDiscrepancyKind, string> = {
  late: "Llegada tarde",
  early_leave: "Salida anticipada",
  overtime: "Hora extra",
  outside_shift: "Fichada fuera de turno",
  orphan: "Fichada huérfana",
  no_clock: "Turno sin fichada",
};

export const TIMECLOCK_KIND_TONE: Record<TimeclockDiscrepancyKind, Tone> = {
  late: "warning",
  early_leave: "warning",
  overtime: "info",
  outside_shift: "danger",
  orphan: "danger",
  no_clock: "danger",
};

/** Qué se computa mientras la discrepancia siga SIN resolver (docs/api/profesores.md §3). */
export const TIMECLOCK_KIND_MEANWHILE: Record<TimeclockDiscrepancyKind, string> = {
  late: "Se computa desde la hora real hasta el fin del turno.",
  early_leave: "Se computa desde el inicio del turno hasta la hora real.",
  overtime: "Se computa el turno, no lo trabajado: la extra la aprueba el coordinador.",
  outside_shift: "Se computa 0: una hora que nadie pidió no se paga hasta que alguien la explique.",
  orphan: "La entrada sin salida se cerró al fin del turno y quedó marcada.",
  no_clock: "Se computa 0: ninguna hora se liquida sin origen registrado.",
};

export const TIMECLOCK_RESOLUTION_LABEL: Record<TimeclockResolution, string> = {
  approve: "Aprobar lo trabajado",
  reject: "Rechazar",
  adjust: "Ajustar los minutos",
};

/** RN-PRO-13/27/28: el detalle clase por clase de una liquidación. */
export const PAYROLL_LINE_KIND_LABEL: Record<PayrollLineKind, string> = {
  class_hour: "Hora de clase",
  sala_hour: "Hora de sala",
  reception_hour: "Hora de recepción",
  premium: "Premio",
  trial_conversion: "Prueba convertida",
  commission: "Comisión",
  retro_adjustment: "Ajuste retroactivo",
};

export const PAYROLL_LINE_KIND_TONE: Record<PayrollLineKind, Tone> = {
  class_hour: "neutral",
  sala_hour: "neutral",
  reception_hour: "neutral",
  premium: "accent",
  trial_conversion: "info",
  commission: "info",
  retro_adjustment: "warning",
};

export const PAYROLL_STATUS_LABEL: Record<PayrollSettlementStatus, string> = {
  draft: "Pre-liquidación",
  in_review: "Revisada por el encargado",
  closed: "Cerrada",
};

export const PAYROLL_STATUS_TONE: Record<PayrollSettlementStatus, Tone> = {
  draft: "neutral",
  in_review: "warning",
  closed: "positive",
};

/** RN-PRO-29: el semáforo de la pre-liquidación. */
export const PAYROLL_FLAG_LABEL: Record<PayrollPendingFlag, string> = {
  unconfirmed_classes: "Clases a confirmar",
  timeclock_discrepancies: "Discrepancias de fichaje",
  external_invoice_missing: "Factura del externo pendiente",
};

export const PAYROLL_FLAG_DETAIL: Record<PayrollPendingFlag, string> = {
  unconfirmed_classes:
    "Hay clases con 0 presentes sin confirmar: no se liquidan hasta que el profe las marque y el coordinador las valide.",
  timeclock_discrepancies:
    "Hay fichadas fuera de la tolerancia sin resolver: se computa lo conservador hasta que el coordinador las resuelva con motivo.",
  external_invoice_missing:
    "El externo todavía no presentó su factura contra el monto de esta liquidación.",
};

export const EXTERNAL_INVOICE_LABEL: Record<ExternalInvoiceStatus, string> = {
  pending: "Factura pendiente",
  received: "Factura recibida",
};

export const EXTERNAL_INVOICE_TONE: Record<ExternalInvoiceStatus, Tone> = {
  pending: "warning",
  received: "positive",
};

/**
 * RN-PRO-12/15: la vara sube sola al cerrar el mes, o la baja una persona con motivo.
 * `migration`: la fijó el pase a la regla del promedio por clase (oct-2026).
 */
export const BAR_SOURCE_LABEL: Record<PerformanceBarSource, string> = {
  auto_close: "Cierre del mes",
  manual: "Ajuste manual",
  migration: "Cambio de regla",
};

/** RN-PRO-24: de dónde salió la atribución de una venta comisionada. */
export const SALE_ORIGIN_LABEL: Record<string, string> = {
  cashier: "Caja del vendedor",
  seller: "Vendedor de la membresía",
  reassigned: "Reasignada con motivo",
};

// §9 Facturación ARCA, egresos, proveedores, convenios y kiosko
// ─────────────────────────────────────────────────────────────────────────────

/** RN-CON-02. `FC`/`NCC`/`NDC` existen en el emisor pero están inactivos siendo RI. */
export const INVOICE_TYPE_LABEL: Record<InvoiceType, string> = {
  FA: "Factura A",
  FB: "Factura B",
  FC: "Factura C",
  NCA: "Nota de crédito A",
  NCB: "Nota de crédito B",
  NCC: "Nota de crédito C",
  NDA: "Nota de débito A",
  NDB: "Nota de débito B",
  NDC: "Nota de débito C",
};

/** Etiqueta corta para la columna densa del listado. */
export const INVOICE_TYPE_SHORT: Record<InvoiceType, string> = {
  FA: "FA",
  FB: "FB",
  FC: "FC",
  NCA: "NC A",
  NCB: "NC B",
  NCC: "NC C",
  NDA: "ND A",
  NDB: "ND B",
  NDC: "ND C",
};

/** RN-CON-06: `pending_cae` es un estado normal, no un error. */
export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  pending_cae: "Pendiente de CAE",
  issued: "Emitido",
  failed: "Falló",
};

export const INVOICE_STATUS_TONE: Record<InvoiceStatus, Tone> = {
  pending_cae: "warning",
  issued: "positive",
  failed: "danger",
};

export const INVOICE_RECEIVER_KIND_LABEL: Record<InvoiceReceiverKind, string> = {
  final_consumer: "Consumidor final",
  identified: "Identificado",
};

export const EXPENSE_METHOD_LABEL: Record<ExpenseMethod, string> = {
  cash_register: "Efectivo de caja",
  transfer: "Transferencia",
  other: "Otro",
};

export const EXPENSE_STATUS_LABEL: Record<ExpenseStatus, string> = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
};

export const EXPENSE_STATUS_TONE: Record<ExpenseStatus, Tone> = {
  pending: "warning",
  approved: "positive",
  rejected: "danger",
};

/** RN-CON-15: mensual fija o por reposición de stock (rel. 9.4). */
export const SUPPLIER_PAYMENT_MODE_LABEL: Record<SupplierPaymentMode, string> = {
  monthly: "Mensual fija",
  on_stock: "Por reposición",
};

/** RN-CON-21: la liquidación avanza y **no retrocede**. */
export const SETTLEMENT_STATUS_LABEL: Record<PlatformSettlementStatus, string> = {
  generated: "Generada",
  sent: "Enviada",
  reported: "Informada por el convenio",
  collected: "Cobrada",
};

export const SETTLEMENT_STATUS_TONE: Record<PlatformSettlementStatus, Tone> = {
  generated: "neutral",
  sent: "info",
  reported: "warning",
  collected: "positive",
};

export const STOCK_MOVEMENT_KIND_LABEL: Record<StockMovementKind, string> = {
  purchase: "Compra",
  sale: "Venta",
  shrinkage: "Merma",
  adjustment: "Ajuste",
  internal_consumption: "Consumo interno",
};

export const STOCK_MOVEMENT_KIND_TONE: Record<StockMovementKind, Tone> = {
  purchase: "positive",
  sale: "neutral",
  shrinkage: "danger",
  adjustment: "warning",
  internal_consumption: "info",
};

/** Cómo se pagó una compra a un proveedor del kiosko. */
export const KIOSK_PURCHASE_METHOD_LABEL: Record<KioskPurchasePaymentMethod, string> = {
  cash_kiosk: "Efectivo de la caja del kiosko",
  transfer: "Transferencia",
  card: "Tarjeta",
  account: "Cuenta corriente del proveedor",
};

export const KIOSK_PURCHASE_STATUS_LABEL: Record<KioskPurchaseStatus, string> = {
  confirmed: "Confirmada",
  voided: "Anulada",
};

export const KIOSK_PURCHASE_STATUS_TONE: Record<KioskPurchaseStatus, Tone> = {
  confirmed: "positive",
  voided: "neutral",
};

/** Por qué un producto está en «Productos a revisar precio». */
export const PRICE_REVIEW_REASON_LABEL: Record<PriceReviewReason, string> = {
  below_cost: "Precio por debajo del costo",
  low_margin: "Margen por debajo del objetivo",
};

/** RN-CON-29: el conteo nace `pending_adjustment` — la diferencia NO ajusta sola. */
export const STOCK_COUNT_STATUS_LABEL: Record<StockCountStatus, string> = {
  pending_adjustment: "Esperando el ajuste",
  adjusted: "Ajustado",
  dismissed: "Desestimado",
};

export const STOCK_COUNT_STATUS_TONE: Record<StockCountStatus, Tone> = {
  pending_adjustment: "warning",
  adjusted: "positive",
  dismissed: "neutral",
};

export const SALE_STATUS_LABEL: Record<SaleStatus, string> = {
  completed: "Cobrada",
  voided: "Anulada",
};

export const SALE_STATUS_TONE: Record<SaleStatus, Tone> = {
  completed: "positive",
  voided: "danger",
};

/** Por dónde entró la venta: el mostrador atendido o la tablet pública con QR. */
export const SALE_CHANNEL_LABEL: Record<SaleChannel, string> = {
  counter: "Mostrador",
  public_tablet: "Tablet pública",
};

/** RN-CON-25: los medios que el kiosko acepta, y ningún otro. */
export const KIOSK_PAYMENT_METHODS: PaymentMethod[] = [
  "cash",
  "mp_point",
  "mp_qr",
  "payway_terminal",
];

/** RN-CON-10: los cuatro exportes del paquete del contador. */
export const ACCOUNTING_EXPORT_LABEL: Record<string, string> = {
  sales_vat_book: "Libro IVA Ventas",
  expenses_book: "Libro de gastos",
  payments_by_method: "Cobros por medio y sede",
  kiosk_sales: "Ventas de kiosko",
};

// ── Integraciones con terceros (`docs/api/integraciones.md`) ─────────────────

/**
 * El estado que la pantalla de Integraciones muestra en la fila.
 *
 * Colapsa **dos ejes** del backend —el estado de la carga (`IntegrationStatus`) y el
 * resultado del último test (`lastTest`)— en el único dato que le importa a quien mira:
 * ¿esto anda, y si no anda, por qué?
 *
 * `test_unmeasured` existe por la disciplina del `null` (CLAUDE.md): una prueba que **no
 * se pudo hacer** no es una prueba fallida. Decirle al dueño que Payway «dio error»
 * cuando en realidad no se pudo medir lo manda a llamar al ejecutivo de cuenta por nada,
 * y —peor— le enseña a desconfiar del semáforo.
 */
export type IntegrationState =
  | "unconfigured"
  | "incomplete"
  | "revoked"
  | "configured"
  | "tested_ok"
  | "tested_error"
  | "test_unmeasured";

export const INTEGRATION_STATE_LABEL: Record<IntegrationState, string> = {
  unconfigured: "Sin configurar",
  incomplete: "Incompleta",
  revoked: "Revocada",
  configured: "Configurada · sin probar",
  tested_ok: "Probada ok",
  tested_error: "Probada con error",
  test_unmeasured: "No se pudo probar",
};

export const INTEGRATION_STATE_TONE: Record<IntegrationState, Tone> = {
  // Sin configurar NO es una falla: es el estado normal antes del alta, con su plan B
  // andando. Pintarlo de rojo sería mentir sobre la salud del sistema.
  unconfigured: "neutral",
  incomplete: "warning",
  revoked: "danger",
  configured: "info",
  tested_ok: "positive",
  tested_error: "danger",
  test_unmeasured: "warning",
};

export const INTEGRATION_ENVIRONMENT_LABEL: Record<IntegrationEnvironment, string> = {
  sandbox: "Sandbox (pruebas)",
  homologacion: "Homologación",
  produccion: "Producción",
};

export const INTEGRATION_ENVIRONMENT_TONE: Record<IntegrationEnvironment, Tone> = {
  sandbox: "neutral",
  homologacion: "neutral",
  // Producción se marca: es la diferencia entre una prueba y una factura de verdad.
  produccion: "accent",
};
